import { computed, onMounted, ref } from 'vue';
import { fetchPublicSiteConfig } from '@/services/siteConfig';
import brand from '@/config/brand';

/** undefined = aún no cargó; null = error / sin datos; object = respuesta API */
export const siteContactPublicRef = ref(undefined);

let loadInflight = null;

function normalizeDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

function formatPhoneDisplay(d) {
  if (!d) return '';
  // AR móvil internacional: 54 9 + área(4) + local(6) → "+54 9 2974 210699"
  if (d.length === 13 && d.startsWith('549')) {
    return `+54 9 ${d.slice(3, 7)} ${d.slice(7)}`.trim();
  }
  // Nacional de 10 dígitos: área(4) + local(6) → "2975 389267"
  if (d.length === 10) {
    return `${d.slice(0, 4)} ${d.slice(4)}`.trim();
  }
  return d;
}

export function loadSiteContactPublic() {
  if (siteContactPublicRef.value !== undefined) {
    return Promise.resolve(siteContactPublicRef.value);
  }
  if (loadInflight) return loadInflight;
  loadInflight = fetchPublicSiteConfig()
    .then((data) => {
      siteContactPublicRef.value = data;
      return data;
    })
    .catch(() => {
      siteContactPublicRef.value = null;
      return null;
    })
    .finally(() => {
      loadInflight = null;
    });
  return loadInflight;
}

function pickConfig() {
  return siteContactPublicRef.value;
}

export function useSiteContact() {
  onMounted(() => {
    loadSiteContactPublic();
  });

  const contactEmail = computed(() => {
    const c = pickConfig();
    if (c && typeof c === 'object') {
      return c.contact_email || c.email || brand.contactEmail;
    }
    return brand.contactEmail;
  });

  const contactPhoneDisplay = computed(() => {
    const c = pickConfig();
    const raw =
      c && typeof c === 'object'
        ? c.contact_phone || c.phone || c.contact_whatsapp
        : '';
    const digits = normalizeDigits(raw);
    const fallbackDigits = normalizeDigits(brand.contactPhone || brand.contactWhatsapp);
    const d = digits || fallbackDigits;
    return formatPhoneDisplay(d);
  });

  const contactMailto = computed(() => {
    const e = contactEmail.value;
    return e ? `mailto:${e}` : '#';
  });

  const whatsappDigits = computed(() => {
    const c = pickConfig();
    const raw =
      c && typeof c === 'object'
        ? c.contact_whatsapp || c.contact_phone || c.phone
        : '';
    const d = normalizeDigits(raw);
    return d || normalizeDigits(brand.contactWhatsapp || brand.contactPhone);
  });

  const whatsappHref = computed(() => {
    const num = whatsappDigits.value;
    if (!num) return '#';
    const c = pickConfig();
    const msg = (c && typeof c === 'object' && c.whatsapp_message) || brand.whatsappMessage;
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  });

  const instagramUrl = computed(() => {
    const c = pickConfig();
    const u = c && typeof c === 'object' ? c.social_instagram : '';
    if (u && String(u).trim()) return String(u).trim();
    return brand.socialInstagram || '';
  });

  const tiktokUrl = computed(() => {
    const c = pickConfig();
    const u = c && typeof c === 'object' ? c.social_tiktok : '';
    if (u && String(u).trim()) return String(u).trim();
    return brand.socialTiktok || '';
  });

  const youtubeUrl = computed(() => {
    const c = pickConfig();
    const u = c && typeof c === 'object' ? c.social_youtube : '';
    if (u && String(u).trim()) return String(u).trim();
    return brand.socialYoutube || '';
  });

  const storeName = computed(() => {
    const c = pickConfig();
    if (c && typeof c === 'object') {
      return c.store_name || brand.storeName;
    }
    return brand.storeName;
  });

  const developerName = computed(() => {
    const c = pickConfig();
    if (c && typeof c === 'object') {
      return c.developer_name || brand.developerName;
    }
    return brand.developerName;
  });

  const developerUrl = computed(() => {
    const c = pickConfig();
    if (c && typeof c === 'object') {
      return c.developer_url || brand.developerUrl;
    }
    return brand.developerUrl;
  });

  const telHref = computed(() => {
    const c = pickConfig();
    const raw =
      c && typeof c === 'object'
        ? c.contact_phone || c.phone || c.contact_whatsapp
        : '';
    const digits = normalizeDigits(raw);
    const d = digits || normalizeDigits(brand.contactPhone || brand.contactWhatsapp);
    return d ? `tel:${d}` : '#';
  });

  return {
    contactEmail,
    contactPhoneDisplay,
    contactMailto,
    whatsappHref,
    instagramUrl,
    tiktokUrl,
    youtubeUrl,
    storeName,
    developerName,
    developerUrl,
    telHref,
  };
}
